import { ChevronDown, AlertTriangle, Wallet } from 'lucide-react'
import { useThemeColors } from '@/hooks/useThemeColors'
import { FONT_BODY } from '@/config/theme'
import { WalletLogos, WALLET_LOGO_KEY } from '@/components/icons/WalletLogos'
import { shortAddr } from '@/utils/format'
import { walletChipAriaName } from '@/utils/walletLabel'

interface WalletChipProps {
  connected: boolean
  address: string | null
  incompatibleChainName: string | null
  missing?: boolean
  walletKind?: string | null
  sourceLabel?: string | null
  /**
   * Fixer pass, F-1 — true when the address this chip renders is the user's OWN connected wallet, as
   * opposed to a pasted / third-party recipient. Defaults to `false` so an OMITTED prop can never
   * silently assert ownership — a caller must opt IN, not rely on a favourable default.
   *
   * The SEND chip is always the connected source wallet (there is no "paste a source" path), so it
   * passes `true` unconditionally. The RECEIVE chip passes `recipientIsConnectedWallet` — `false` for
   * a pasted third-party address, exactly the case `destWalletKind` already nulls out for the brand
   * icon (ADDENDUM A4). This prop is the SAME signal for the accessible name, independent of
   * `walletKind`: `walletKind` answers "do we know the BRAND", this answers "is it THEIRS", and the
   * two questions have different answers on the receive chip for a pasted address whose connector
   * happens to be unmapped (both would read `null`/unknown under the old, collapsed logic).
   */
  isOwnWallet?: boolean
  onClick: () => void
  disabled?: boolean
}

export function WalletChip({
  connected,
  address,
  incompatibleChainName,
  missing,
  walletKind,
  sourceLabel,
  isOwnWallet = false,
  onClick,
  disabled,
}: WalletChipProps) {
  const c = useThemeColors()
  const isIncompatible = !!incompatibleChainName

  // Styling priority: disconnected → warning (missing/incompatible) → connected
  let bg: string
  let brd: string
  let txtColor: string

  if (!connected) {
    bg = 'transparent'; brd = `1px solid ${c.ac}`; txtColor = c.ac
  } else if (missing || isIncompatible) {
    bg = c.wB; brd = `1px solid ${c.wC}55`; txtColor = c.wC
  } else {
    bg = c.iB; brd = `1px solid ${c.dv}`; txtColor = c.tx
  }

  const key = walletKind ? WALLET_LOGO_KEY[walletKind] : null
  const Logo = key ? WalletLogos[key] : null

  /**
   * #261 F3 / ADDENDUM A5 — ships UNFLAGGED. Additive, display-only, no fund path.
   *
   * Without it this button's accessible name is whatever its children happen to concatenate to — a
   * truncated address and a chevron — so a screen-reader user hears `0x7d775A…A149D02D` twice on the
   * card with nothing distinguishing the SEND chip from the RECEIVE one, which is precisely the
   * "which wallet is this?" confusion F3 exists to remove. The brand icon F3 adds is an `<svg>`/`<img>`
   * with no accessible text, so the visual cue that now carries the answer is invisible here unless
   * the name states it.
   *
   * The truncated address is included because it is already rendered visibly; no new information is
   * disclosed. `walletKind` is a canonical name from the shared allowlist, never a guess (H6), so it
   * is omitted rather than approximated when unknown.
   *
   * ── FIXER PASS, F-1: `isOwnWallet` GATES THE OWNERSHIP CLAIM, NOT `walletKind` ───────────────────
   * `walletKind === null` used to mean two different things at once — "unknown connector" (own wallet,
   * unmapped brand) AND "this is a pasted third-party recipient" (A4 passes `null` there too) — and
   * both collapsed onto the same `Connected wallet` reading, which asserts custody a pasted recipient
   * never has. A screen-reader user pasting an exchange deposit address heard "Connected wallet ·
   * 0x06Dc…D02Dabc" on the row labelled "You receive" — the same false reassurance
   * `ReviewTransferModal/index.tsx`'s "Not your connected wallet" warning exists to prevent, in a
   * different modality. `isOwnWallet` is the explicit, independent signal that disambiguates the two:
   *   · own wallet, known kind   → names the wallet      (`walletChipAriaName(walletKind)`)
   *   · own wallet, unknown kind → truthful non-specific  (`Connected wallet`)
   *   · NOT own wallet           → never claims ownership (`Recipient address`), regardless of
   *                                 `walletKind` — defensive: today's callers always pair `false` here
   *                                 with a `null` kind (A4), but the ownership check must hold even if
   *                                 that pairing were ever broken.
   *
   * ── FIXER PASS, G-1 / G-2: `walletChipAriaName` ROUTES THROUGH THE SAME TRANSPORT RULE ───────────
   * The wallet-name half is no longer `${walletKind} wallet` inline — that hardcoded a second
   * "WalletConnect names a wallet" claim (contradicting `buildRecipientWalletLabel`'s `via
   * WalletConnect` transport phrasing, ADDENDUM A5) and a second noun-doubling guard was simply
   * missing (`Coinbase Wallet wallet`). `walletChipAriaName` (`utils/walletLabel.ts`) is the ONE place
   * both rules live now; see it for the WalletConnect/Coinbase-Wallet-specific reasoning.
   */
  const ariaLabel = !connected
    ? 'Connect a wallet'
    : missing
      ? 'Set a recipient address'
      : isIncompatible
        ? `Switch wallet — ${incompatibleChainName} is not available in the connected wallet`
        : [
            isOwnWallet
              ? (walletChipAriaName(walletKind) ?? 'Connected wallet')
              : 'Recipient address',
            shortAddr(address),
            sourceLabel || null,
          ].filter(Boolean).join(' · ')

  return (
    <button
      onClick={onClick}
      disabled={disabled}
      aria-label={ariaLabel}
      style={{
        display: 'flex', alignItems: 'center', gap: 5,
        padding: '5px 9px 5px 6px',
        borderRadius: 10, background: bg, border: brd,
        cursor: disabled ? 'default' : 'pointer',
        fontFamily: FONT_BODY, fontSize: 12, fontWeight: 500,
        color: txtColor, transition: 'all 0.15s',
        whiteSpace: 'nowrap', flexShrink: 0,
      }}
    >
      {!connected ? (
        <>
          <Wallet size={12} />
          <span style={{ fontWeight: 600 }}>Connect</span>
        </>
      ) : missing ? (
        <>
          <AlertTriangle size={12} />
          <span style={{ fontWeight: 600 }}>Set recipient</span>
        </>
      ) : isIncompatible ? (
        <>
          <AlertTriangle size={12} />
          <span style={{ fontWeight: 600 }}>Switch wallet</span>
        </>
      ) : (
        <>
          {Logo ? <Logo size={14} /> : <Wallet size={12} />}
          <span style={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600, letterSpacing: 0.1 }}>
            {shortAddr(address)}
          </span>
          {sourceLabel && (
            <span style={{ color: c.mu, fontWeight: 500, fontSize: 11, marginLeft: 1 }}>&middot; {sourceLabel}</span>
          )}
        </>
      )}
      <ChevronDown
        size={10}
        style={{ color: (!connected || missing || isIncompatible) ? (connected ? c.wC : c.mu) : c.mu, marginLeft: 1 }}
        aria-hidden="true"
      />
    </button>
  )
}
